import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from "../../../universal/utils";
import adminFetch from "../../../universal/adminFetch";
import useIsMountedRef from "../../../hooks/useIsMountedRef";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  select: {
    minWidth: 260,
  },
  status: {
    marginTop: theme.spacing(1),
  }
}));

function getProviderLabel(option) {
  return option?.label || option?.providerId || 'Unknown provider';
}

export default function MailProviderSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mailProvider, setMailProvider] = useState('gmailSmtp');
  const [fromAddress, setFromAddress] = useState('no-reply@ouro.is');
  const [availableProviders, setAvailableProviders] = useState([]);
  const [updatedAtMs, setUpdatedAtMs] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/mail-provider/adpass/${userId}`);
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load mail provider setting');
      if (!isMountedRef.current) return;
      setMailProvider(data.mailProvider || 'gmailSmtp');
      setFromAddress(data.fromAddress || 'no-reply@ouro.is');
      setAvailableProviders(Array.isArray(data.availableProviders) ? data.availableProviders : []);
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : null);
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    if (!userId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/mail-provider/adpass/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mailProvider })
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to save mail provider setting');
      if (!isMountedRef.current) return;
      setMailProvider(data.mailProvider || 'gmailSmtp');
      setFromAddress(data.fromAddress || 'no-reply@ouro.is');
      setAvailableProviders(Array.isArray(data.availableProviders) ? data.availableProviders : []);
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : Date.now());
      setSuccess(
        data.mailProvider === 'sesApi'
          ? 'Saved. New outgoing emails will use Amazon SES via AWS.'
          : 'Saved. New outgoing emails will use Gmail SMTP. Local development still defaults to Gmail by default.'
      );
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [basePath, isMountedRef, mailProvider, userId]);

  const activeProvider = availableProviders.find((option) => option.providerId === mailProvider) || null;

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Mail Provider</Typography>
          <Typography variant="body2" color="textSecondary">
            Choose between Amazon SES (API) and Gmail SMTP for outgoing edge emails. Production
            mail uses the shared sender <strong>{fromAddress}</strong>. Local development still
            defaults to Gmail, but Amazon SES (API) can be smoke-tested separately or selected for
            end-to-end verification.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box className={classes.row}>
          <FormControl variant="outlined" size="small" className={classes.select} disabled={loading || saving || !userId}>
            <Select
              value={mailProvider}
              onChange={(event) => setMailProvider(event.target.value)}
            >
              {availableProviders.map((option) => (
                <MenuItem key={option.providerId} value={option.providerId}>
                  {getProviderLabel(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={loading || saving || !userId}
          >
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>

          <Button
            variant="outlined"
            onClick={load}
            disabled={loading || saving || !userId}
          >
            Refresh
          </Button>
        </Box>

        {activeProvider ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Active: {getProviderLabel(activeProvider)}. {activeProvider.enabled ? 'Configured.' : (activeProvider.reason || 'Not configured.')}
          </Typography>
        ) : null}

        {availableProviders.length > 0 ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Available providers: {availableProviders.map((option) => getProviderLabel(option)).join(', ')}
          </Typography>
        ) : null}

        {updatedAtMs ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Last updated: {new Date(updatedAtMs).toISOString()}
          </Typography>
        ) : null}

        {loading ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Loading...
          </Typography>
        ) : null}

        {error ? (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {success ? (
          <Typography className={classes.status} variant="body2" style={{ color: '#1b5e20' }}>
            {success}
          </Typography>
        ) : null}
      </Collapse>
    </Paper>
  );
}
